import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { format } from 'date-fns';
import ru from 'date-fns/locale/ru';
import type { fabric } from 'fabric';
import { DEFAULT_OWN_GOAL_FILL } from 'modules/manager/domain/placeholder/goal-scorers-layout';
import { PlaceholderKey } from 'modules/manager/domain/placeholder/placeholder-keys';
import {
  DEFAULT_PLAYER_NAME_FORMAT,
  PLAYER_NAME_FORMAT_OPTIONS,
  PLAYER_NAME_FORMAT_SAMPLE,
  PlayerNameFormat,
  formatPlayerNameParts,
} from 'modules/manager/domain/placeholder/player-name-format';
import { defaultDateFormatFor, isDateKey } from 'modules/manager/domain/placeholder/date-format';
import type { GoalScorerEventKind, PlaceholderOverflowMode } from 'modules/manager/domain/placeholder/types';
import type { MessageDescriptor } from 'shared/i18n';
import { DEFAULT_SCORE_FORMAT } from 'modules/manager/domain/placeholder/resolver';
import React, { useCallback } from 'react';
import { editorAccent } from 'modules/manager/ui/components/editor/editor-accent';
import { registerInspector } from './inspector-registry';
import PlaceholderTransformSection from './placeholder-transform-section';
import ColorField from './shared/color-field';
import CommitNumberField from './shared/commit-number-field';
import CommitTextField from './shared/commit-text-field';
import FontLoadWarning from 'shared/components/font-load-warning';
import HelpBubble from './shared/help-bubble';
import InspectorCheckbox from './shared/inspector-checkbox';
import InspectorRow from './shared/inspector-row';
import InspectorSection from './shared/inspector-section';
import MixedSelectField from './shared/mixed-select-field';
import PlaceholderIdentitySection from './shared/placeholder-identity-section';
import VisibleWhenSection from './shared/visible-when-section';
import { useCommonFabricData } from './shared/use-common-fabric-data';
import { useFontFields } from './shared/use-font-fields';
import type { ObjectInspectorProps } from './types';
import { theme } from 'styles/theme/theme';

const TEXT_ALIGNS = [
  { value: 'left', label: msg`Влево` },
  { value: 'center', label: msg`По центру` },
  { value: 'right', label: msg`Вправо` },
];

const VERTICAL_ALIGNS = [
  { value: 'top', label: msg`Сверху` },
  { value: 'center', label: msg`По центру` },
  { value: 'bottom', label: msg`Снизу` },
];

const OVERFLOW_MODES: { value: PlaceholderOverflowMode; label: MessageDescriptor }[] = [
  { value: 'auto', label: msg`Авто: шрифт → колонки → «+N»` },
  { value: 'shrink', label: msg`Уменьшать шрифт` },
  { value: 'columns', label: msg`Две колонки` },
  { value: 'truncate', label: msg`Обрезать с «+N»` },
];

const GOAL_SCORER_EVENT_KINDS: { value: GoalScorerEventKind; label: MessageDescriptor }[] = [
  { value: 'goal', label: msg`Голы` },
  { value: 'penalty', label: msg`Пенальти` },
  { value: 'ownGoal', label: msg`Автоголы` },
  { value: 'shootout', label: msg`Серия пенальти` },
];

const PlaceholderTextInspector: React.FC<ObjectInspectorProps> = ({ objects, editor }) => {
  const { t } = useLingui();
  const MIXED = t`(разное)`;
  const previewMode = useCommonFabricData<boolean>(objects, 'previewMode', editor);
  const sampleText = useCommonFabricData<string>(objects, 'sampleText', editor);
  const fontFamily = useCommonFabricData<string>(objects, 'fontFamily', editor);
  const fontWeight = useCommonFabricData<string>(objects, 'fontWeight', editor);
  const textAlign = useCommonFabricData<string>(objects, 'textAlign', editor);
  const fill = useCommonFabricData<string>(objects, 'fill', editor);
  const minFontSize = useCommonFabricData<number>(objects, 'minFontSize', editor);
  const maxFontSize = useCommonFabricData<number>(objects, 'maxFontSize', editor);
  const dateFormat = useCommonFabricData<string>(objects, 'dateFormat', editor);
  const scoreFormat = useCommonFabricData<string>(objects, 'scoreFormat', editor);
  const nameFormat = useCommonFabricData<string>(objects, 'nameFormat', editor);
  const placeholderKey = useCommonFabricData<string>(objects, 'placeholderKey', editor);
  const verticalAlign = useCommonFabricData<string>(objects, 'verticalAlign', editor);
  const overflowMode = useCommonFabricData<string>(objects, 'overflowMode', editor);
  const ownGoalFill = useCommonFabricData<string>(objects, 'ownGoalFill', editor);
  const ownGoalMark = useCommonFabricData<boolean>(objects, 'ownGoalMark', editor);
  const hiddenEventKinds = useCommonFabricData<GoalScorerEventKind[]>(objects, 'hiddenEventKinds', editor);

  // Format fields only make sense when the whole selection shares the key.
  const showDateFormat = !placeholderKey.isMixed && isDateKey(placeholderKey.value);
  const defaultDateFormat = defaultDateFormatFor(placeholderKey.value);
  const showScoreFormat = !placeholderKey.isMixed && placeholderKey.value === PlaceholderKey.SCORE;
  const showGoalScorers = !placeholderKey.isMixed && placeholderKey.value === PlaceholderKey.GOAL_SCORERS;
  const showNameFormat = !placeholderKey.isMixed && (placeholderKey.value === PlaceholderKey.PLAYER_NAME || showGoalScorers);
  // The goal list defaults to «Нигматулин Р.» — the league references' format.
  const nameFormatFallback = showGoalScorers ? PlayerNameFormat.SURNAME_NAME_INITIAL : DEFAULT_PLAYER_NAME_FORMAT;

  // Always `ru`, whatever the interface language: the example shows how the date
  // will look in the rendered image, and the image keeps Russian dates
  // (`manager/domain/placeholder/resolver.ts` is outside the translation boundary).
  const dateFormatExample = (() => {
    try {
      return format(new Date(), dateFormat.value || defaultDateFormat, { locale: ru });
    } catch {
      return 'Invalid format';
    }
  })();

  const sampleName = formatPlayerNameParts(PLAYER_NAME_FORMAT_SAMPLE);
  const textAligns = TEXT_ALIGNS.map((option) => ({ value: option.value, label: t(option.label) }));

  const refitAll = useCallback(() => {
    for (const o of objects) editor.placeholders?.refit(o as fabric.Group);
  }, [editor, objects]);
  const fonts = useFontFields(fontFamily, fontWeight, refitAll, 'Arial');

  const handlePreviewToggle = () => {
    const next = previewMode.isMixed ? true : !(previewMode.value ?? false);
    previewMode.setValue(next || undefined);
    refitAll();
  };

  const sampleDisabled = !previewMode.value && !previewMode.isMixed;

  const verticalAligns = VERTICAL_ALIGNS.map((option) => ({ value: option.value, label: t(option.label) }));
  const overflowModes = OVERFLOW_MODES.map((option) => ({ value: option.value, label: t(option.label) }));
  // Arrays are compared by reference, so a multi-selection reads as mixed;
  // a toggle then starts from «everything shown».
  const hiddenKinds = hiddenEventKinds.isMixed ? [] : (hiddenEventKinds.value ?? []);
  const isOwnGoalMarked = ownGoalMark.value ?? true;

  const toggleEventKind = (kind: GoalScorerEventKind) => {
    const next = hiddenKinds.includes(kind) ? hiddenKinds.filter((k) => k !== kind) : [...hiddenKinds, kind];
    hiddenEventKinds.setValue(next.length > 0 ? next : undefined);
    refitAll();
  };

  return (
    <>
      <InspectorSection title={t`Предпросмотр`}>
        <InspectorRow label={t`Режим предпросмотра`}>
          <InspectorCheckbox
            checked={Boolean(previewMode.value) && !previewMode.isMixed}
            indeterminate={previewMode.isMixed}
            onChange={handlePreviewToggle}
            label={previewMode.isMixed ? MIXED : t`показать только образец текста`}
          />
        </InspectorRow>
      </InspectorSection>
      <PlaceholderIdentitySection objects={objects} editor={editor} />
      <VisibleWhenSection objects={objects} editor={editor} />
      {(showDateFormat || showScoreFormat || showNameFormat) && (
        <InspectorSection title={t`Формат`}>
          {showDateFormat && (
            <InspectorRow label={t`Дата`}>
              <FieldWithHelp
                help={
                  <span>
                    <Trans>
                      Формат даты с использованием{' '}
                      <a
                        href="https://date-fns.org/docs/format"
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: editorAccent.ink, textDecoration: 'underline' }}
                      >
                        токенов date-fns
                      </a>
                      . Пример: <code>dd MMMM, yyyy</code>. Оставьте пустым для формата по умолчанию: <code>{defaultDateFormat}</code>.
                    </Trans>
                  </span>
                }
              >
                <CommitTextField
                  value={dateFormat.isMixed ? '' : (dateFormat.value ?? '')}
                  onCommit={(v) => {
                    dateFormat.setValue(v ? v : undefined);
                    refitAll();
                  }}
                  placeholder={dateFormat.isMixed ? MIXED : defaultDateFormat}
                />
              </FieldWithHelp>
            </InspectorRow>
          )}
          {showDateFormat && (
            <InspectorRow label={t`Пример`}>
              <CommitTextField value={dateFormatExample} onCommit={() => {}} disabled />
            </InspectorRow>
          )}
          {showScoreFormat && (
            <InspectorRow label={t`Счёт`}>
              <FieldWithHelp
                help={
                  <span>
                    <Trans>
                      Шаблон итогового счёта. <code>t1</code> = счёт хозяев, <code>t2</code> = счёт гостей. Примеры:{' '}
                      <code>t1 - t2</code>, <code>t1:t2</code>, <code>t1</code>. Оставьте пустым для формата по умолчанию:{' '}
                      <code>{DEFAULT_SCORE_FORMAT}</code>.
                    </Trans>
                  </span>
                }
              >
                <CommitTextField
                  value={scoreFormat.isMixed ? '' : (scoreFormat.value ?? '')}
                  onCommit={(v) => {
                    scoreFormat.setValue(v ? v : undefined);
                    refitAll();
                  }}
                  placeholder={scoreFormat.isMixed ? MIXED : DEFAULT_SCORE_FORMAT}
                />
              </FieldWithHelp>
            </InspectorRow>
          )}
          {showNameFormat && (
            <InspectorRow label={t`ФИО`}>
              <FieldWithHelp
                help={
                  <span>
                    <Trans>
                      Как разложить имя игрока. Варианты показаны на примере <code>{sampleName}</code>. Если у игрока нет отчества,
                      соответствующая часть просто не выводится — лишних точек и пробелов не остаётся.
                    </Trans>
                  </span>
                }
              >
                <MixedSelectField
                  value={nameFormat.value}
                  isMixed={nameFormat.isMixed}
                  onChange={(v) => {
                    nameFormat.setValue(v);
                    refitAll();
                  }}
                  options={[...PLAYER_NAME_FORMAT_OPTIONS]}
                  fallback={nameFormatFallback}
                />
              </FieldWithHelp>
            </InspectorRow>
          )}
        </InspectorSection>
      )}
      {showGoalScorers && (
        <InspectorSection title={t`Авторы голов`}>
          <InspectorRow label={t`По вертикали`}>
            <MixedSelectField
              value={verticalAlign.value}
              isMixed={verticalAlign.isMixed}
              onChange={(v) => {
                verticalAlign.setValue(v);
                refitAll();
              }}
              options={verticalAligns}
              fallback="center"
            />
          </InspectorRow>
          <InspectorRow label={t`Если не помещается`}>
            <MixedSelectField
              value={overflowMode.value}
              isMixed={overflowMode.isMixed}
              onChange={(v) => {
                overflowMode.setValue(v);
                refitAll();
              }}
              options={overflowModes}
              fallback="auto"
            />
          </InspectorRow>
          <InspectorRow label={t`Цвет автогола`}>
            <ColorField
              value={ownGoalFill.isMixed ? undefined : (ownGoalFill.value ?? DEFAULT_OWN_GOAL_FILL)}
              onChange={(v) => {
                ownGoalFill.setValue(v);
                refitAll();
              }}
              placeholder={ownGoalFill.isMixed ? MIXED : undefined}
            />
          </InspectorRow>
          <InspectorRow label={t`Пометка (АГ)`}>
            <InspectorCheckbox
              checked={isOwnGoalMarked && !ownGoalMark.isMixed}
              onChange={() => {
                // `true` is the default, so it is stored as «no value».
                ownGoalMark.setValue(ownGoalMark.isMixed || !isOwnGoalMarked ? undefined : false);
                refitAll();
              }}
              label={ownGoalMark.isMixed ? MIXED : t`дописывать «(АГ)» к автоголу`}
            />
          </InspectorRow>
          <InspectorRow label={t`Показывать события`}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {GOAL_SCORER_EVENT_KINDS.map((kind) => (
                <InspectorCheckbox
                  key={kind.value}
                  checked={!hiddenKinds.includes(kind.value)}
                  onChange={() => toggleEventKind(kind.value)}
                  label={t(kind.label)}
                />
              ))}
            </div>
          </InspectorRow>
        </InspectorSection>
      )}
      <InspectorSection title={t`Образец текста`}>
        <InspectorRow label={t`Текст`}>
          <CommitTextField
            value={sampleText.isMixed ? '' : sampleText.value}
            onCommit={(v) => {
              sampleText.setValue(v);
              refitAll();
            }}
            multiline
            placeholder={sampleText.isMixed ? MIXED : t`Текст для предпросмотра`}
            disabled={sampleDisabled}
          />
        </InspectorRow>
      </InspectorSection>
      <InspectorSection title={t`Типографика`}>
        <InspectorRow label={t`Шрифт`}>
          <div className="flex items-center gap-1.5">
            <div className="min-w-0 flex-1">
              <MixedSelectField
                value={fontFamily.value}
                isMixed={fontFamily.isMixed}
                onChange={fonts.changeFamily}
                options={fonts.familyOptions}
                fallback="Arial"
              />
            </div>
            {!fontFamily.isMixed && <FontLoadWarning fontFamily={fontFamily.value} fontWeight={fontWeight.value} />}
          </div>
        </InspectorRow>
        <InspectorRow label={t`Начертание`}>
          <MixedSelectField
            value={fonts.weightValue}
            isMixed={fontWeight.isMixed}
            onChange={fonts.changeWeight}
            options={fonts.weightOptions}
            fallback="400"
          />
        </InspectorRow>
        <InspectorRow label={t`Выравнивание`}>
          <MixedSelectField
            value={textAlign.value}
            isMixed={textAlign.isMixed}
            onChange={(v) => {
              textAlign.setValue(v);
              refitAll();
            }}
            options={textAligns}
            fallback="center"
          />
        </InspectorRow>
        <InspectorRow label={t`Цвет`}>
          <ColorField
            value={fill.isMixed ? undefined : (fill.value ?? theme.colors.DarkBlue600)}
            onChange={(v) => {
              fill.setValue(v);
              refitAll();
            }}
            placeholder={fill.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
      </InspectorSection>
      <InspectorSection title={t`Вписывание`}>
        <InspectorRow label={t`Мин. размер`}>
          <CommitNumberField
            value={minFontSize.isMixed ? undefined : (minFontSize.value ?? 8)}
            onCommit={(v) => {
              minFontSize.setValue(v);
              refitAll();
            }}
            min={1}
            placeholder={minFontSize.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
        <InspectorRow label={t`Макс. размер`}>
          <CommitNumberField
            value={maxFontSize.isMixed ? undefined : (maxFontSize.value ?? 256)}
            onCommit={(v) => {
              maxFontSize.setValue(v);
              refitAll();
            }}
            min={1}
            placeholder={maxFontSize.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
      </InspectorSection>
      <PlaceholderTransformSection objects={objects} editor={editor} />
    </>
  );
};

export default PlaceholderTextInspector;

registerInspector('placeholder-text', PlaceholderTextInspector);

const FieldWithHelp: React.FC<{ help: React.ReactNode; children: React.ReactNode }> = ({ help, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', minWidth: 0 }}>
    <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    <HelpBubble content={help} />
  </div>
);
